// WikiBlock.jsx — renders one Notion-style block (13 types), read + edit modes.
// Edit-mode text commits on blur (avoids contentEditable cursor jumps). Supports
// a diffTone ('added' | 'removed') for the side-by-side version diff.
import React from 'react';
import {
    Sparkles, Info, Flag, LifeBuoy, FileText, AlertTriangle, Check,
    ChevronRight, ChevronDown, Video,
} from 'lucide-react';

// Transform a YouTube / Loom / Vimeo URL into a whitelisted embeddable iframe src.
// Returns null for anything unrecognized (no arbitrary iframes).
export function embedSrc(raw) {
    if (!raw) return null;
    const url = String(raw).trim();
    let m;
    m = url.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
    if (m) return { provider: 'YouTube', src: `https://www.youtube.com/embed/${m[1]}` };
    m = url.match(/loom\.com\/(?:share|embed)\/([\w-]+)/);
    if (m) return { provider: 'Loom', src: `https://www.loom.com/embed/${m[1]}` };
    m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (m) return { provider: 'Vimeo', src: `https://player.vimeo.com/video/${m[1]}` };
    return null;
}

const CALLOUT_TONES = {
    violet: 'border-violet-200 bg-violet-50/60 text-violet-900',
    indigo: 'border-indigo-200 bg-indigo-50/60 text-indigo-900',
    amber: 'border-amber-200 bg-amber-50/60 text-amber-900',
    cyan: 'border-cyan-200 bg-cyan-50/60 text-cyan-900',
};
const CALLOUT_ICON_COLOR = { violet: 'text-violet-500', indigo: 'text-indigo-500', amber: 'text-amber-500', cyan: 'text-cyan-500' };
const ICONS = { sparkles: Sparkles, info: Info, flag: Flag, 'life-buoy': LifeBuoy, 'file-text': FileText, 'alert-triangle': AlertTriangle };

function diffClass(tone) {
    if (tone === 'added') return 'bg-emerald-50 -mx-2 px-2 rounded';
    if (tone === 'removed') return 'bg-rose-50 -mx-2 px-2 rounded line-through decoration-rose-300/70';
    return '';
}

// Inline editable text — commits on blur.
function Editable({ value, editing, onCommit, className, placeholder }) {
    if (!editing) return <span className={className}>{value}</span>;
    return (
        <span
            contentEditable suppressContentEditableWarning
            data-placeholder={placeholder}
            onBlur={(e) => onCommit(e.currentTarget.textContent)}
            className={`${className} outline-none focus:bg-stone-100/60 rounded px-0.5 -mx-0.5 empty:before:content-[attr(data-placeholder)] empty:before:text-stone-300`}
        >{value}</span>
    );
}

export default function WikiBlock({ b, editing = false, diffTone = null, onChange }) {
    const p = b.props || {};
    const ring = diffClass(diffTone);
    const commit = (patch) => onChange && onChange(b.block_id, { ...p, ...patch });

    switch (b.block_type) {
        case 'h1':
            return <h1 className={`wiki-display font-extrabold text-stone-900 text-[26px] tracking-tight mt-5 ${ring}`}>
                <Editable value={p.text} editing={editing} onCommit={(t) => commit({ text: t })} className="" placeholder="Heading 1" /></h1>;
        case 'h2':
            return <h2 className={`wiki-display font-bold text-stone-900 text-[18px] tracking-tight mt-5 ${ring}`}>
                <Editable value={p.text} editing={editing} onCommit={(t) => commit({ text: t })} className="" placeholder="Heading 2" /></h2>;
        case 'text':
            return <p className={`text-[14.5px] text-stone-600 leading-relaxed mt-2.5 ${ring}`}>
                <Editable value={p.text} editing={editing} onCommit={(t) => commit({ text: t })} className="" placeholder="Type something…" /></p>;
        case 'bullet':
            return <div className={`flex gap-2.5 mt-1.5 text-[14.5px] text-stone-600 leading-relaxed ${ring}`}>
                <span className="text-stone-400 mt-0.5">•</span>
                <Editable value={p.text} editing={editing} onCommit={(t) => commit({ text: t })} className="flex-1" placeholder="List item" /></div>;
        case 'num':
            return <div className={`flex gap-2.5 mt-1.5 text-[14.5px] text-stone-600 leading-relaxed ${ring}`}>
                <span className="text-stone-400 font-mono">›</span>
                <Editable value={p.text} editing={editing} onCommit={(t) => commit({ text: t })} className="flex-1" placeholder="List item" /></div>;
        case 'check':
            return (
                <div className={`flex items-center gap-2.5 mt-1.5 text-[14.5px] leading-relaxed ${ring}`}>
                    <button type="button" disabled={!editing} onClick={() => commit({ done: !p.done })}
                        className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${p.done ? 'bg-stone-900 border-stone-900 text-white' : 'border-stone-300'}`}>
                        {p.done && <Check className="w-3 h-3" />}</button>
                    <Editable value={p.text} editing={editing} onCommit={(t) => commit({ text: t })}
                        className={p.done ? 'text-stone-500 line-through' : 'text-stone-600'} placeholder="To-do" /></div>);
        case 'callout': {
            const Ico = ICONS[p.icon] || Info;
            return (
                <div className={`flex gap-2.5 border rounded-xl p-3 mt-3 ${CALLOUT_TONES[p.tone] || CALLOUT_TONES.indigo}`}>
                    <Ico className={`w-4 h-4 mt-0.5 shrink-0 ${CALLOUT_ICON_COLOR[p.tone] || CALLOUT_ICON_COLOR.indigo}`} />
                    <div className="text-[13px] leading-relaxed flex-1">
                        <Editable value={p.text} editing={editing} onCommit={(t) => commit({ text: t })} className="" placeholder="Callout text" />
                        {editing && (
                            <div className="flex gap-1 mt-2">
                                {['violet', 'indigo', 'amber', 'cyan'].map(t => (
                                    <button key={t} type="button" onClick={() => commit({ tone: t })}
                                        className={`w-4 h-4 rounded-full border ${CALLOUT_ICON_COLOR[t]} ${p.tone === t ? 'ring-2 ring-offset-1 ring-stone-400' : ''}`}
                                        style={{ background: 'currentColor' }} title={t} />
                                ))}
                            </div>
                        )}
                    </div>
                </div>);
        }
        case 'quote':
            return <blockquote className={`border-l-2 border-stone-300 pl-4 mt-3 text-[15px] italic text-stone-600 ${ring}`}>
                <Editable value={p.text} editing={editing} onCommit={(t) => commit({ text: t })} className="" placeholder="Quote" /></blockquote>;
        case 'divider':
            return <hr className="my-5 border-stone-200" />;
        case 'code':
            return <pre className="bg-stone-900 text-stone-100 rounded-lg p-3 mt-3 text-[12px] font-mono overflow-auto">
                <Editable value={p.text} editing={editing} onCommit={(t) => commit({ text: t })} className="" placeholder="code" /></pre>;
        case 'toggle':
            return <ToggleBlock p={p} editing={editing} commit={commit} />;
        case 'table':
            return <TableBlock p={p} editing={editing} commit={commit} />;
        case 'image':
            return (
                <div className="mt-3">
                    {p.url ? (
                        <img src={p.url} alt={p.alt || p.caption || ''} className="rounded-lg border border-stone-200 max-w-full" />
                    ) : (
                        <div className="rounded-lg border border-stone-200 overflow-hidden h-40 flex items-center justify-center"
                            style={{ background: 'repeating-linear-gradient(45deg,#F1F5F9,#F1F5F9 10px,#E2E8F0 10px,#E2E8F0 20px)' }}>
                            <span className="font-mono text-[11px] text-stone-500 bg-white/80 px-2 py-1 rounded">▦ {p.caption || 'image'}</span>
                        </div>
                    )}
                    {editing && (
                        <input value={p.url || ''} onChange={(e) => commit({ url: e.target.value })} placeholder="Image URL"
                            className="mt-1.5 w-full h-8 px-2 rounded border border-stone-200 text-[12px]" />
                    )}
                </div>);
        case 'embed': {
            const e = embedSrc(p.url);
            return (
                <div className="mt-3">
                    {e ? (
                        <div className="rounded-lg overflow-hidden border border-stone-200 bg-black aspect-video">
                            <iframe src={e.src} title={p.caption || e.provider} className="w-full h-full" frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                        </div>
                    ) : (
                        <div className="rounded-lg border border-dashed border-stone-300 bg-stone-50 h-32 flex items-center justify-center text-center px-4">
                            <span className="text-[12px] text-stone-500 inline-flex items-center gap-1.5"><Video className="w-4 h-4 text-stone-400" />
                                {p.url ? 'Unsupported link — paste a YouTube, Loom, or Vimeo URL.' : 'Paste a YouTube, Loom, or Vimeo link to embed a video.'}</span>
                        </div>
                    )}
                    {p.caption && !editing && <div className="text-[11px] text-stone-400 mt-1">{p.caption}</div>}
                    {editing && (
                        <div className="mt-1.5 space-y-1.5">
                            <input value={p.url || ''} onChange={(ev) => commit({ url: ev.target.value })} placeholder="YouTube / Loom / Vimeo URL"
                                className="w-full h-8 px-2 rounded border border-stone-200 text-[12px]" />
                            <input value={p.caption || ''} onChange={(ev) => commit({ caption: ev.target.value })} placeholder="Caption (optional)"
                                className="w-full h-8 px-2 rounded border border-stone-200 text-[12px]" />
                        </div>
                    )}
                </div>);
        }
        default:
            return null;
    }
}

function ToggleBlock({ p, editing, commit }) {
    const [open, setOpen] = React.useState(false);
    return (
        <div className="mt-2">
            <button type="button" onClick={() => setOpen(o => !o)} className="flex items-center gap-1.5 text-[14.5px] font-medium text-stone-900">
                {open ? <ChevronDown className="w-3.5 h-3.5 text-stone-400" /> : <ChevronRight className="w-3.5 h-3.5 text-stone-400" />}
                <Editable value={p.text} editing={editing} onCommit={(t) => commit({ text: t })} className="" placeholder="Toggle title" />
            </button>
            {open && (
                <div className="ml-5 mt-1 text-[14px] text-stone-600 leading-relaxed border-l border-stone-200 pl-3">
                    <Editable value={p.body} editing={editing} onCommit={(t) => commit({ body: t })} className="" placeholder="Hidden content" />
                </div>
            )}
        </div>
    );
}

function TableBlock({ p, editing, commit }) {
    const head = p.head || [];
    const rows = p.rows || [];
    const setCell = (ri, ci, val) => {
        const next = rows.map(r => [...r]);
        next[ri][ci] = val;
        commit({ rows: next });
    };
    const setHead = (ci, val) => { const h = [...head]; h[ci] = val; commit({ head: h }); };
    return (
        <div className="mt-3 rounded-lg border border-stone-200 overflow-hidden">
            <table className="w-full text-[13px]">
                <thead className="bg-stone-100"><tr>
                    {head.map((hd, i) => (
                        <th key={i} className="text-left font-semibold text-stone-600 px-3 py-2 border-b border-stone-200">
                            <Editable value={hd} editing={editing} onCommit={(t) => setHead(i, t)} className="" /></th>
                    ))}
                </tr></thead>
                <tbody>{rows.map((r, ri) => (
                    <tr key={ri} className="border-b border-stone-200 last:border-b-0">
                        {r.map((c, ci) => (
                            <td key={ci} className={`px-3 py-2 ${ci === 0 ? 'font-medium text-stone-900' : 'text-stone-600'}`}>
                                <Editable value={c} editing={editing} onCommit={(t) => setCell(ri, ci, t)} className="" /></td>
                        ))}
                    </tr>
                ))}</tbody>
            </table>
            {editing && (
                <div className="flex gap-2 p-2 bg-stone-50 border-t border-stone-200">
                    <button type="button" onClick={() => commit({ rows: [...rows, head.map(() => '')] })}
                        className="text-[11px] font-semibold text-stone-900 hover:text-stone-700">+ Row</button>
                    <button type="button" onClick={() => commit({ head: [...head, `Column ${head.length + 1}`], rows: rows.map(r => [...r, '']) })}
                        className="text-[11px] font-semibold text-stone-900 hover:text-stone-700">+ Column</button>
                </div>
            )}
        </div>
    );
}
